import { Box, Stack, SxProps, Theme } from '@mui/material';
import { forwardRef, ReactNode } from 'react';

type StableActionRowProps = {
  /**
   * Innehåll som ligger till vänster (t.ex. statistik/hälsning). Får dyka
   * upp/försvinna utan att höger-slotten flyttar sig — slotten behåller sin
   * bredd via flex.
   */
  start?: ReactNode;
  /**
   * Den primära kontrollen/knappen. Ligger ALLTID på exakt samma plats
   * (högerkanten) oavsett om `start` finns eller inte.
   */
  end: ReactNode;
  sx?: SxProps<Theme>;
};

/**
 * En rad med en stabil vänster-slot och en stabil höger-slot.
 *
 * INVARIANT: höger-kontrollen (`end`) byter ALDRIG position bara för att
 * vänster-innehållet (`start`) dyker upp eller försvinner. Tidigare använde
 * vyerna `justifyContent: space-between` med två syskon, vilket fick
 * höger-knappen att glida åt vänster så fort vänster-barnet kollapsade
 * (t.ex. när antalet avklarade aktiviteter var 0). Här reserverar
 * vänster-slotten alltid sitt utrymme (`flex: 1`), så endast dess synliga
 * innehåll ändras — layouten flödar inte om.
 */
const StableActionRow = forwardRef<HTMLDivElement, StableActionRowProps>(
  ({ start, end, sx }, ref) => {
    return (
      <Stack ref={ref} direction="row" alignItems="center" sx={sx}>
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center' }}>{start}</Box>
        <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>{end}</Box>
      </Stack>
    );
  },
);

StableActionRow.displayName = 'StableActionRow';

export default StableActionRow;
